/**
 * CheckIdentityEditor — edit the fields a check asked the payer about, and
 * re-run it, without leaving the screen (#314).
 *
 * ONE component, mounted by every surface that displays a check's identity, so
 * the capability cannot exist on one screen and be missing on another — that
 * inconsistency is what teaches staff to stop looking for it.
 *
 * It shows what was SENT, not the patient record: the operator is correcting
 * the question that was asked. Saving fires a corrected check in the same flow
 * and hands the result back to the parent; nothing navigates.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import DateInput from './DateInput'
import PayerSearch, { type SelectedPayer } from './PayerSearch'
import {
  CORRECTABLE_FIELDS,
  FIELD_LABELS,
  changedFields,
  correctionPayload,
  draftFromCheck,
  offersPayerMapping,
  unreadableDob,
  withPayerPick,
  type CorrectableCheck,
  type CorrectableField,
  type CorrectionDraft,
} from '../utils/checkCorrection'
import { correctAndRecheck, CorrectCheckUnrunnable } from '../services/correctCheck'
import { setPayerAlias } from '../services/payerAliases'
import type { CorrectCheckOutcome } from '../services/correctCheck'

export interface CheckIdentityEditorProps {
  orgId: string
  check: CorrectableCheck
  /** Fired after a corrected check was created — the parent follows the flow.
   *  Every caller tears the panel down here, so it is the "we are done" signal,
   *  not the "it happened" one. */
  onCorrected: (outcome: Extract<CorrectCheckOutcome, { kind: 'fired' }>) => void
  /**
   * Fired the moment the corrected check exists, before any clinic-mapping
   * offer is answered — for parents to refresh what they are showing.
   *
   * Without it, a correction followed by closing the panel left the operator
   * looking at the check they had just corrected, unchanged: the outcome was
   * parked for the offer, the close discarded it, and `onCorrected` — the only
   * thing that refreshed anything — never fired.
   */
  onFired?: () => void
  /** View-mode staff and read-only surfaces pass false. */
  canWrite?: boolean
  /** Registers a "you have unsaved edits" check with the parent so closing the
   *  panel can warn instead of silently discarding typed work (#274). */
  onDirtyChange?: (dirty: boolean) => void
}

export default function CheckIdentityEditor({
  orgId,
  check,
  onCorrected,
  onFired,
  canWrite = true,
  onDirtyChange,
}: CheckIdentityEditorProps) {
  const original = useMemo(() => draftFromCheck(check), [check])
  // A stored date of birth the date control cannot render (#322). Named rather
  // than left blank: a blank box is how an operator concludes we never had the
  // value, on the one case that exists because that value is disputed.
  const unreadableDate = useMemo(() => unreadableDob(check), [check])
  const [draft, setDraft] = useState<CorrectionDraft>(original)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  /** The draft has been committed — see the dirty-reporting effect below. */
  const [saved, setSaved] = useState(false)
  // Guards the double-submit the ticket calls out: the backend dedupes through
  // the check cache, but a second in-flight call is still a wasted round trip.
  const inFlight = useRef(false)
  // The directory pick behind the payer name — an alias needs the trading
  // partner id, not the display text.
  const [pickedPayer, setPickedPayer] = useState<SelectedPayer | null>(null)
  // The clinic-wide mapping offer (#314): shown only AFTER the correction ran,
  // and only as its own explicit confirmation. A check-level fix must never
  // silently become a rule for every future patient with that shorthand.
  const [mappingOffer, setMappingOffer] = useState<
    { shorthand: string; payer: SelectedPayer } | null
  >(null)
  const [mappingDone, setMappingDone] = useState<string | null>(null)
  // Every caller tears this panel down inside `onCorrected`, so announcing the
  // correction and setting the offer in the same tick meant React unmounted
  // before the offer ever painted — the confirmation was unreachable in the
  // shipped UI. The outcome is PARKED here instead and announced once the
  // operator has answered the offer (or a write failure has been shown).
  const [pendingCorrected, setPendingCorrected] = useState<
    Extract<CorrectCheckOutcome, { kind: 'fired' }> | null
  >(null)

  const dirtyFields = changedFields(original, draft)
  const dirty = dirtyFields.length > 0

  const setField = useCallback((field: CorrectableField, value: string) => {
    setDraft(prev => ({ ...prev, [field]: value }))
    setNotice(null)
    setError(null)
  }, [])

  // Reported from an effect, never from inside the setDraft updater. React may
  // run an updater during the render phase (and twice under StrictMode), so
  // calling the parent's setter in there is a setState-while-rendering — React
  // logs it as an error, and with the #274 guard there are now TWO parents
  // being written to mid-render. Deriving it from committed state also means
  // the flag can't disagree with what the fields actually hold.
  const reportDirty = onDirtyChange
  useEffect(() => {
    // A saved correction still differs from `original` — that prop is the
    // pre-correction check until the parent refetches — but it is not unsaved
    // work, and warning about it would train operators to ignore the warning.
    reportDirty?.(dirty && !saved)
  }, [dirty, saved, reportDirty])

  async function save() {
    // `saved` is in the guard as well as on the button: `original` is the
    // PRE-correction check and never updates, so `dirty` stays true after a
    // successful save and the button re-enables the moment `saving` clears.
    // A second click fires a second correction off the same source — and if
    // anything was edited in between, the source ends up superseded by the
    // second while the first stays live, two successors of one visit, both
    // counted.
    if (!dirty || saving || saved || inFlight.current) return
    inFlight.current = true
    setSaving(true)
    setError(null)
    setNotice(null)
    try {
      // A picked payer carries its ids (#349): authoritative on the backend,
      // never re-resolved through a clinic alias or the usage ranker.
      const outcome = await correctAndRecheck(
        orgId, check.id, withPayerPick(correctionPayload(original, draft), pickedPayer),
      )
      if (outcome.kind === 'no_change') {
        // The values differ on screen but ask the payer the same question
        // (a member id retyped with different separators, say). Say so rather
        // than pretending a check ran.
        setNotice(outcome.message
          ?? 'Those values ask the payer the same question — no new check was needed.')
        return
      }
      setSaved(true)
      // Announce the FACT immediately; the offer below decides only when the
      // panel closes.
      onFired?.()
      // The raw shorthand the check was created from is what an alias keys on —
      // what the source file/EHR said, not what this screen now displays. With
      // no shorthand or no directory pick there is nothing to map, and we stay
      // quiet rather than guessing.
      const shorthand = (check.requestPayerName ?? '').trim()
      if (
        offersPayerMapping(original, draft) && shorthand &&
        pickedPayer?.tradingPartnerServiceId && check.clinicId
      ) {
        setMappingOffer({ shorthand, payer: pickedPayer })
        setPendingCorrected(outcome)
        return
      }
      onCorrected(outcome)
    } catch (err) {
      setError(
        err instanceof CorrectCheckUnrunnable
          ? err.message
          : 'Could not re-run the check. Your edits are still here — try again.',
      )
    } finally {
      inFlight.current = false
      setSaving(false)
    }
  }

  /** Announce the parked correction — the parent closes the panel here. */
  function announceCorrected() {
    const outcome = pendingCorrected
    setPendingCorrected(null)
    if (outcome) onCorrected(outcome)
  }

  function reset() {
    // The effect reports the cleared state on its own.
    setDraft(original)
    setError(null)
    setNotice(null)
  }

  return (
    <div className="check-edit">
      <div className="check-edit__head">
        <h4 className="check-edit__title">What we asked the payer</h4>
        {dirty && <span className="check-edit__dirty">{dirtyFields.length} edited</span>}
      </div>

      <div className="check-edit__grid">
        {CORRECTABLE_FIELDS.map(field => {
          const id = `check-edit-${check.id}-${field}`
          const edited = dirtyFields.includes(field)
          return (
            <label key={field} className="check-edit__field" htmlFor={id}>
              <span className="check-edit__label">
                {FIELD_LABELS[field]}
                {edited && <span className="check-edit__label-edited"> · edited</span>}
              </span>
              {field === 'payerName' ? (
                <PayerSearch
                  inputId={id}
                  value={draft.payerName}
                  disabled={!canWrite || saving}
                  clinicId={check.clinicId ?? undefined}
                  // This row already says "Payer"; the control's own "Insurance"
                  // label stacked a second one on the same field. And the
                  // default grey input read as LOCKED between four white
                  // siblings — same field, same dress (#318 review).
                  hideLabel
                  inputClassName={`check-edit__input${edited ? ' check-edit__input--edited' : ''}`}
                  onSelect={p => { setPickedPayer(p); setField('payerName', p.name) }}
                  onClear={() => { setPickedPayer(null); setField('payerName', '') }}
                />
              ) : field === 'dateOfBirth' ? (
                <>
                  <DateInput
                    id={id}
                    className={`check-edit__input${edited ? ' check-edit__input--edited' : ''}`}
                    value={draft.dateOfBirth}
                    disabled={!canWrite || saving}
                    onValueChange={v => setField('dateOfBirth', v)}
                  />
                  {unreadableDate && !edited && (
                    <span className="check-edit__hint">
                      Stored as &ldquo;{unreadableDate}&rdquo; — not a date this field can
                      show. Type it to correct it; left blank, the check keeps what it sent.
                    </span>
                  )}
                </>
              ) : (
                <input
                  id={id}
                  className={`check-edit__input${edited ? ' check-edit__input--edited' : ''}`}
                  value={draft[field]}
                  disabled={!canWrite || saving}
                  onChange={e => setField(field, e.target.value)}
                />
              )}
            </label>
          )
        })}
      </div>

      {notice && <p className="check-edit__notice">{notice}</p>}
      {error && <p className="check-edit__error">{error}</p>}

      <div className="check-edit__actions">
        <button
          type="button"
          className="check-edit__save"
          onClick={save}
          disabled={!canWrite || !dirty || saving || saved}
        >
          {saving ? 'Re-checking…' : 'Save & re-check'}
        </button>
        {dirty && !saving && (
          <button type="button" className="check-edit__reset" onClick={reset}>
            Discard edits
          </button>
        )}
      </div>

      {mappingOffer && check.clinicId && (
        <div className="check-edit__mapping" role="status">
          <p className="check-edit__mapping-text">
            This clinic&apos;s <strong>{mappingOffer.shorthand}</strong> still resolves
            to the old payer. Map it to <strong>{mappingOffer.payer.name}</strong> for
            every future check?
          </p>
          <div className="check-edit__mapping-actions">
            <button
              type="button"
              className="check-edit__mapping-yes"
              onClick={async () => {
                const offer = mappingOffer
                setMappingOffer(null)
                try {
                  await setPayerAlias({
                    clinicId: check.clinicId as string,
                    shorthand: offer.shorthand,
                    route: 'stedi',
                    tradingPartnerServiceId: offer.payer.tradingPartnerServiceId,
                    stediPayerId: offer.payer.stediId,
                    resolvedName: offer.payer.name,
                  })
                  announceCorrected()
                } catch {
                  // The check IS fixed; only the clinic-wide rule failed. Say so
                  // and keep the panel up — closing here would report a mapping
                  // that does not exist.
                  setMappingDone('This one check was fixed, but the clinic mapping could not be saved.')
                }
              }}
            >
              Map it for this clinic
            </button>
            <button
              type="button"
              className="check-edit__mapping-no"
              onClick={() => { setMappingOffer(null); announceCorrected() }}
            >
              Just this check
            </button>
          </div>
        </div>
      )}
      {mappingDone && (
        <div className="check-edit__mapping" role="status">
          <p className="check-edit__mapping-text">{mappingDone}</p>
          {pendingCorrected && (
            <button
              type="button"
              className="check-edit__mapping-no"
              onClick={announceCorrected}
            >
              Continue
            </button>
          )}
        </div>
      )}
    </div>
  )
}
